// CommentThread — flat comment list + add-comment form for a post.
//
// Renders:
//   1. The existing flat comment thread (from PostDetailData.comments) in
//      chronological order (id ascending — as returned by the API).
//   2. An "Add a comment" form for authenticated users.
//
// Flat, non-threaded comments only in v1 (PRD §10 prototype assumption,
// story: comments).  Threaded / nested comments are deferred to a later version.
//
// Design source: design/project/scripts/pages.jsx comment section,
// design/project/CitiDev Hub.html (pd-comments, pd-comment rows).
//
// API:
//   POST /api/v1/posts/{post_id}/comments
//   — requires authenticated session cookie.
//   — 422 for empty / whitespace-only text.
//
// Voting on comments is handled by the VoteControl component (voting story).
// This component renders the vote count only (read-only in v1 — the voting
// story owns the interactive upvote control).

import { type FormEvent, useCallback, useState } from "react";
import { apiClient } from "@/lib/api-client";
import type { CommentItem } from "./PostDetail";
import { ReactionBar } from "./ReactionBar";
import type { ReactionSummary } from "./reactions-api";
import { GifPicker } from "./GifPicker";
import type { GifResult } from "./gifs-api";
import { isSafeHttpUrl } from "@/lib/url";

// ---------------------------------------------------------------------------
// API types (mirror api/schemas/comments.py CommentCreate + CommentResponse)
// ---------------------------------------------------------------------------

export interface CommentCreate {
  text: string;
  /** Optional GIF attachment (from the GIF picker) — an addition, not a replacement. */
  gif_url?: string | null;
  gif_title?: string | null;
}

/** Request shape for PUT /api/v1/posts/{post_id}/comments/{id}. */
export type CommentUpdate = CommentCreate;

export interface CommentResponse {
  id: number;
  post_id: string;
  author_id: string | null;
  /** Resolved from the Member profile at creation time (AC3, story: comments). */
  author_name?: string | null;
  /** Two-letter initials from the Member profile. */
  author_initials?: string | null;
  /** Division badge from the Member profile (NBS | NFS | NDS | SS). */
  author_division?: string | null;
  text: string;
  vote_count: number;
  created_at: string; // ISO 8601
  /** Set only when the comment was edited after creation. */
  updated_at?: string | null;
  gif_url?: string | null;
  gif_title?: string | null;
  reactions?: ReactionSummary[];
}

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/posts/{post_id}/comments
 *
 * Creates a comment attributed to the authenticated session user.
 * Returns the created comment with id, post_id, author_id, text, vote_count,
 * and created_at.
 *
 * Throws ApiError on 401 (unauthenticated) or 422 (invalid text).
 */
export async function createComment(
  postId: string,
  data: CommentCreate,
): Promise<CommentResponse> {
  return apiClient<CommentResponse>(
    `/api/v1/posts/${encodeURIComponent(postId)}/comments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

/**
 * PUT /api/v1/posts/{post_id}/comments/{id}
 *
 * Edit an existing comment (author self-service, or admin moderation). The
 * server enforces that the caller is the comment's author OR an admin (403
 * otherwise). Returns the updated comment, including `updated_at`.
 */
export async function editComment(
  postId: string,
  commentId: number,
  data: CommentUpdate,
): Promise<CommentResponse> {
  return apiClient<CommentResponse>(
    `/api/v1/posts/${encodeURIComponent(postId)}/comments/${commentId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
}

/**
 * DELETE /api/v1/posts/{post_id}/comments/{id}
 *
 * Delete a comment (author self-service, or admin moderation). The server
 * enforces that the caller is the comment's author OR an admin (403
 * otherwise). Deletion is immediate and permanent — there is no undo.
 */
export async function deleteComment(postId: string, commentId: number): Promise<void> {
  await apiClient<{ removed: boolean; comment_id: number }>(
    `/api/v1/posts/${encodeURIComponent(postId)}/comments/${commentId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Renders a single flat comment row.
 *  Matches the pd-comment structure in design/project/scripts/pages.jsx.
 *  Displays author_name + author_division badge (AC3, story: comments).
 *  Falls back to author_id if name is not yet resolved. */
function CommentRow({ comment }: { comment: CommentItem }) {
  // Prefer resolved author_name; fall back to author_id for legacy data.
  const displayName = comment.author_name ?? comment.author_id ?? null;

  return (
    <div
      className="pd-comment"
      data-testid="comment-row"
      data-comment-id={comment.id}
      role="article"
      aria-label="Comment"
    >
      <div className="pd-comment__meta">
        {displayName && (
          <span
            className="pd-comment__author"
            aria-label="Comment author"
          >
            {comment.author_initials && (
              <span className="pd-comment__initials" aria-hidden="true">
                {comment.author_initials}
              </span>
            )}
            {displayName}
          </span>
        )}
        {comment.author_division && (
          <span
            className="pd-comment__division"
            aria-label={`Division: ${comment.author_division}`}
          >
            {comment.author_division}
          </span>
        )}
        <time
          className="pd-comment__time"
          dateTime={comment.created_at}
          aria-label={`Posted ${timeAgo(comment.created_at)}`}
        >
          {timeAgo(comment.created_at)}
        </time>
      </div>
      {comment.text && <div className="pd-comment__text">{comment.text}</div>}
      {comment.gif_url && isSafeHttpUrl(comment.gif_url) && (
        <img
          className="pd-comment-gif"
          src={comment.gif_url}
          alt={comment.gif_title ?? "Attached GIF"}
          loading="lazy"
          data-testid="comment-gif"
        />
      )}
      <div className="pd-comment__foot">
        {/* Vote count — read-only display; the voting story owns the interactive control */}
        <span
          className="pd-comment__votes"
          aria-label={`${comment.vote_count} ${comment.vote_count === 1 ? "vote" : "votes"}`}
        >
          ▲ {comment.vote_count}
        </span>
        <ReactionBar
          target="comment"
          targetId={String(comment.id)}
          initialReactions={comment.reactions ?? []}
        />
      </div>
    </div>
  );
}

/** Add-comment form.  Shows validation error for empty/whitespace text. */
interface AddCommentFormProps {
  onSubmit: (text: string, gif: GifResult | null) => Promise<void>;
  submitting: boolean;
  error: string | null;
}

function AddCommentForm({ onSubmit, submitting, error }: AddCommentFormProps) {
  const [text, setText] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [stagedGif, setStagedGif] = useState<GifResult | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLocalError(null);

      const trimmed = text.trim();
      // A comment needs content — either text or an attached GIF. A GIF on its
      // own (no text) is allowed.
      if (!trimmed && !stagedGif) {
        setLocalError("Add a comment or attach a GIF.");
        return;
      }

      await onSubmit(trimmed, stagedGif);
      setText("");
      setStagedGif(null);
    },
    [text, stagedGif, onSubmit],
  );

  const displayError = localError ?? error;

  return (
    <form
      className="pd-add-comment"
      onSubmit={handleSubmit}
      data-testid="add-comment-form"
      aria-label="Add a comment"
    >
      <label htmlFor="comment-text" className="pd-add-comment__label">
        Add a comment
      </label>
      <textarea
        id="comment-text"
        className="pd-add-comment__input"
        data-testid="comment-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share your thoughts…"
        rows={3}
        disabled={submitting}
        aria-describedby={displayError ? "comment-error" : undefined}
        aria-invalid={displayError != null ? "true" : undefined}
      />
      {stagedGif && (
        <div className="gif-staged" data-testid="staged-gif">
          <img src={stagedGif.preview_url} alt={stagedGif.title} />
          <button
            type="button"
            className="gif-staged__remove"
            aria-label="Remove attached GIF"
            onClick={() => setStagedGif(null)}
            data-testid="staged-gif-remove"
          >
            ×
          </button>
        </div>
      )}
      {displayError && (
        <p
          id="comment-error"
          className="pd-add-comment__error"
          role="alert"
          data-testid="comment-error"
        >
          {displayError}
        </p>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <GifPicker onSelect={setStagedGif} />
        <button
          type="submit"
          className="pd-add-comment__submit"
          data-testid="comment-submit"
          disabled={submitting}
          aria-disabled={submitting}
        >
          {submitting ? "Posting…" : "Post comment"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// CommentThread — main export
// ---------------------------------------------------------------------------

export interface CommentThreadProps {
  /** The post id — used to POST new comments to the correct endpoint. */
  postId: string;
  /**
   * Initial flat comment list (from PostDetailData.comments).
   * Sorted chronologically (id ASC) by the API; the component preserves that
   * order and appends optimistically on successful submission.
   */
  initialComments: CommentItem[];
}

/**
 * CommentThread renders the flat comment thread for a post and provides
 * an "add comment" form for authenticated users.
 *
 * Comments are flat (non-threaded) in v1.  New comments are appended
 * optimistically after successful API submission.
 *
 * The component does not guard authentication — the parent (PostDetail) must
 * ensure the user is authenticated.  The API itself enforces auth and will
 * return 401 if the session is absent, which is surfaced as an error message.
 */
export function CommentThread({
  postId,
  initialComments,
}: CommentThreadProps) {
  const [comments, setComments] = useState<CommentItem[]>(initialComments);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (text: string, gif: GifResult | null) => {
      setSubmitting(true);
      setSubmitError(null);

      try {
        const created = await createComment(postId, {
          text,
          gif_url: gif?.url ?? null,
          gif_title: gif?.title ?? null,
        });
        // Append the new comment to the end (chronological order preserved)
        const appended: CommentItem = {
          id: created.id,
          author_id: created.author_id,
          author_name: created.author_name,
          author_initials: created.author_initials,
          author_division: created.author_division,
          text: created.text,
          vote_count: created.vote_count,
          created_at: created.created_at,
          gif_url: created.gif_url,
          gif_title: created.gif_title,
          reactions: created.reactions ?? [],
        };
        setComments((prev) => [...prev, appended]);
      } catch (err: unknown) {
        if (err && typeof err === "object" && "status" in err) {
          const status = (err as { status: number }).status;
          if (status === 401) {
            setSubmitError("You must be signed in to comment.");
          } else if (status === 422) {
            setSubmitError("Comment text must not be empty.");
          } else {
            setSubmitError("Could not post your comment. Please try again.");
          }
        } else {
          setSubmitError("Could not post your comment. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [postId],
  );

  return (
    <section
      className="pd-comments"
      data-testid="comments-section"
      aria-label="Comments"
    >
      <h3 className="pd-comments__heading">
        Comments{" "}
        <span
          className="pd-comments__count"
          aria-label={`${comments.length} ${comments.length === 1 ? "comment" : "comments"}`}
        >
          {comments.length}
        </span>
      </h3>

      {/* Flat comment list — rendered in chronological (id ASC) order */}
      {comments.length === 0 ? (
        <p
          className="pd-comments__empty"
          data-testid="comments-empty"
        >
          No comments yet — start the thread.
        </p>
      ) : (
        <div
          className="pd-comments__list"
          data-testid="comments-list"
          role="list"
          aria-label="Comment list"
        >
          {comments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </div>
      )}

      {/* Add-comment form — always shown; API enforces auth */}
      <AddCommentForm
        onSubmit={handleSubmit}
        submitting={submitting}
        error={submitError}
      />
    </section>
  );
}

export default CommentThread;
